import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export function Calendar({
  selected,
  onSelect,
  disableFuture = false,
  mode = "single", // kept for API parity
  initialFocus,
  startYear, // optional override
  endYear, // optional override
}) {
  const today = useMemo(() => new Date(), []);
  const baseYear = today.getFullYear();

  // Default year range: last 100 years to +50 (or current year if disableFuture)
  const minYear = startYear ?? baseYear - 100;
  const maxYear = endYear ?? (disableFuture ? baseYear : baseYear + 50);

  const [currentDate, setCurrentDate] = useState(selected || new Date());

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const firstDay = new Date(year, month, 1);
  const startDate = new Date(firstDay);
  startDate.setDate(startDate.getDate() - firstDay.getDay());

  const days = [];
  const cur = new Date(startDate);
  for (let i = 0; i < 42; i++) {
    days.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }

  const navigateMonth = (direction) => {
    const newDate = new Date(year, month + direction, 1);
    setCurrentDate(newDate);
  };

  const handleDateSelect = (date) => {
    if (onSelect) onSelect(date);
  };

  const isSameDay = (a, b) =>
    a &&
    b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const atStartOfDay = (d) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate());

  const handleMonthChange = (e) => {
    const m = Number(e.target.value);
    setCurrentDate(new Date(year, m, 1));
  };

  const handleYearChange = (e) => {
    const y = Number(e.target.value);
    setCurrentDate(new Date(y, month, 1));
  };

  return (
    <div className="p-3">
      {/* Header with navigation and selectors */}
      <div className="mb-3 flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigateMonth(-1)}
          className="rounded-md p-1 hover:bg-gray-100"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-2">
          <select
            aria-label="Select month"
            value={month}
            onChange={handleMonthChange}
            className="h-8 rounded-md border border-gray-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
          >
            {monthNames.map((name, idx) => (
              <option value={idx} key={name}>
                {name}
              </option>
            ))}
          </select>

          <select
            aria-label="Select year"
            value={year}
            onChange={handleYearChange}
            className="h-8 rounded-md border border-gray-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
          >
            {Array.from(
              { length: maxYear - minYear + 1 },
              (_, i) => minYear + i
            ).map((y) => (
              <option value={y} key={y}>
                {y}
              </option>
            ))}
          </select>
        </div>

        <button
          type="button"
          onClick={() => navigateMonth(1)}
          className="rounded-md p-1 hover:bg-gray-100"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Days of week */}
      <div className="mb-2 grid grid-cols-7 gap-1">
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
          <div
            key={d}
            className="py-2 text-center text-xs font-medium text-gray-500"
          >
            {d}
          </div>
        ))}
      </div>

      {/* Days grid */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((day, idx) => {
          const isCurrentMonth = day.getMonth() === month;
          const todayFlag = isSameDay(day, today);
          const selectedFlag = isSameDay(day, selected);
          const isDisabled =
            disableFuture && atStartOfDay(day) > atStartOfDay(today);

          return (
            <button
              key={idx}
              type="button"
              onClick={() => !isDisabled && handleDateSelect(day)}
              disabled={isDisabled}
              className={[
                "rounded-md p-2 text-sm transition-colors",
                isCurrentMonth ? "text-gray-900" : "text-gray-300",
                todayFlag
                  ? "bg-blue-100 text-blue-600 font-medium"
                  : "hover:bg-gray-100",
                selectedFlag ? "bg-gray-900 text-white hover:bg-gray-800" : "",
                isDisabled
                  ? "cursor-not-allowed opacity-40 hover:bg-transparent"
                  : "",
              ].join(" ")}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}
